import { useEffect, useState } from 'react'
import { KTIcon } from '../../../helpers'
import { useParams } from 'react-router-dom'
import { getRequest } from '../../../../app/pages/Helpers/Requests'
import PaginatedItems from '../../../../app/UI/PaginatedItems'

export default function OcpiCdr() {
  let { pointId } = useParams()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [cdrs, setCdrs] = useState<any[]>([])
  const [page, setPage] = useState<number>(1)
  const [limit] = useState<number>(10)
  const [pages, setPages] = useState<number>(1)

  const fetchData = async () => {
    try {
      const response = await getRequest(`/ocpi/msp/${pointId}/cdrs?page=${page}&limit=${limit}`)
      if (response && response.data) {
        console.log('Fetched CDRs:', response.data)
        setCdrs(response.data.data)
        setPages(response.data.pagination?.totalPages || 1)
      }
    } catch (error) {
      console.error('Error fetching CDRs:', error)
    }
  }

  useEffect(() => {
    fetchData()
  }, [page])

  // Format date for display
  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    if (amount === null || amount === undefined) return 'N/A'
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  // Render charging periods
  const renderChargingPeriods = (periods: any[]) => {
    if (!periods || periods.length === 0) return 'No charging periods'
    
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-bordered table-striped align-middle gs-0 gy-3'>
          <thead className='table-light'>
            <tr className='fw-bold text-muted bg-light'>
              <th className='min-w-150px ps-4 pe-4 py-3 text-uppercase'>START TIME</th>
              <th className='min-w-120px ps-4 pe-4 py-3 text-uppercase'>DIMENSION TYPE</th>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>VOLUME</th>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>TARIFF ID</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((period, index) => (
              <tr key={index} className='border-bottom border-gray-200'>
                <td className='ps-4 pe-4 py-3'>{formatDateTime(period.start_date_time)}</td>
                <td className='ps-4 pe-4 py-3'>
                  {period.dimensions?.map((dim: any, dimIndex: number) => (
                    <div key={dimIndex} className='mb-1'>
                      <span className='badge badge-light-primary fs-7 fw-bold'>{dim.type}</span>
                    </div>
                  ))}
                </td>
                <td className='ps-4 pe-4 py-3'>
                  {period.dimensions?.map((dim: any, dimIndex: number) => (
                    <div key={dimIndex} className='mb-1'>
                      <span className='fw-bold text-primary'>{dim.volume} {dim.type === 'ENERGY' ? 'kWh' : ''}</span>
                    </div>
                  ))}
                </td>
                <td className='ps-4 pe-4 py-3'>
                  <span className='badge badge-light-info fs-7 fw-bold'>{period.tariff_id || 'N/A'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Render CDR token details
  const renderCdrToken = (token: any) => {
    if (!token) return 'No token information'
    
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-bordered table-striped align-middle gs-0 gy-2'>
          <tbody>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>COUNTRY CODE</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-success fs-7 fw-bold'>{token.country_code || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>PARTY ID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-dark'>{token.party_id || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>UID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-primary'>{token.uid || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>TYPE</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-info fs-7 fw-bold'>{token.type || 'N/A'}</span>
              </td>
            </tr>
            <tr>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>CONTRACT ID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-warning'>{token.contract_id || 'N/A'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  // Render CDR location details
  const renderCdrLocation = (location: any) => {
    if (!location) return 'No location information'
    
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-bordered table-striped align-middle gs-0 gy-2'>
          <tbody>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>LOCATION ID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-dark'>{location.id || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>NAME</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-primary'>{location.name || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>CITY</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-info'>{location.city || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>STATE</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-warning'>{location.state || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>POSTAL CODE</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-success'>{location.postal_code || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>COUNTRY</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-success fs-7 fw-bold'>{location.country || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>COORDINATES</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-dark'>
                  {location.coordinates ? 
                    `${location.coordinates.latitude}, ${location.coordinates.longitude}` : 'N/A'}
                </span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>EVSE UID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-primary'>{location.evse_uid || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>EVSE ID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-info'>{location.evse_id || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>CONNECTOR ID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-warning'>{location.connector_id || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>CONNECTOR STANDARD</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-primary fs-7 fw-bold'>{location.connector_standard || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>CONNECTOR FORMAT</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-info fs-7 fw-bold'>{location.connector_format || 'N/A'}</span>
              </td>
            </tr>
            <tr>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>CONNECTOR POWER TYPE</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-success fs-7 fw-bold'>{location.connector_power_type || 'N/A'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  // Render tariffs
  const renderTariffs = (tariffs: any[]) => {
    if (!tariffs || tariffs.length === 0) return 'No tariff information'
    
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-bordered table-striped align-middle gs-0 gy-3'>
          <thead className='table-light'>
            <tr className='fw-bold text-muted bg-light'>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>TARIFF ID</th>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>CURRENCY</th>
              <th className='min-w-120px ps-4 pe-4 py-3 text-uppercase'>PRICE TYPE</th>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>PRICE</th>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>VAT</th>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>STEP SIZE</th>
            </tr>
          </thead>
          <tbody>
            {tariffs.map((tariff, index) => (
              <tr key={index} className='border-bottom border-gray-200'>
                <td className='ps-4 pe-4 py-3'>
                  <span className='badge badge-light-primary fs-7 fw-bold'>{tariff.id || 'N/A'}</span>
                </td>
                <td className='ps-4 pe-4 py-3'>
                  <span className='badge badge-light-success fs-7 fw-bold'>{tariff.currency || 'N/A'}</span>
                </td>
                <td className='ps-4 pe-4 py-3'>
                  {tariff.elements?.[0]?.price_components?.map((comp: any, compIndex: number) => (
                    <div key={compIndex} className='mb-1'>
                      <span className='badge badge-light-info fs-7 fw-bold'>{comp.type}</span>
                    </div>
                  ))}
                </td>
                <td className='ps-4 pe-4 py-3'>
                  {tariff.elements?.[0]?.price_components?.map((comp: any, compIndex: number) => (
                    <div key={compIndex} className='mb-1'>
                      <span className='fw-bold text-primary'>₹{comp.price}</span>
                    </div>
                  ))}
                </td>
                <td className='ps-4 pe-4 py-3'>
                  {tariff.elements?.[0]?.price_components?.map((comp: any, compIndex: number) => (
                    <div key={compIndex} className='mb-1'>
                      <span className='fw-bold text-warning'>{comp.vat}%</span>
                    </div>
                  ))}
                </td>
                <td className='ps-4 pe-4 py-3'>
                  {tariff.elements?.[0]?.price_components?.map((comp: any, compIndex: number) => (
                    <div key={compIndex} className='mb-1'>
                      <span className='fw-bold text-success'>{comp.step_size}</span>
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Render total cost details
  const renderTotalCost = (cost: any) => {
    if (!cost) return 'No cost information'
    
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-bordered table-striped align-middle gs-0 gy-2'>
          <tbody>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>EXCLUDING VAT</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-success fs-6'>{formatCurrency(cost.excl_vat, cost.currency)}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>INCLUDING VAT</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-primary fs-6'>{formatCurrency(cost.incl_vat, cost.currency)}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>VAT AMOUNT</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-warning fs-6'>{formatCurrency(cost.vat, cost.currency)}</span>
              </td>
            </tr>
            <tr>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>CURRENCY</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-success fs-7 fw-bold'>{cost.currency || 'N/A'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  // Render CDR details
  const renderCdrDetails = (cdr: any) => {
    return (
      <div className='p-6 bg-light rounded'>
        <div className='row g-5'>
          {/* Basic Information */}
          <div className='col-md-6'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-primary text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>BASIC INFORMATION</h6>
              </div>
              <div className='card-body p-4'>
                <div className='table-responsive'>
                  <table className='table table-sm table-borderless align-middle gs-0 gy-2'>
                    <tbody>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>COUNTRY CODE</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-success fs-7 fw-bold'>{cdr.country_code || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>PARTY ID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-dark'>{cdr.party_id || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>CDR ID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-primary fs-7 fw-bold'>{cdr.id || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>SESSION ID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-primary'>{cdr.session_id || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>AUTH METHOD</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-info fs-7 fw-bold'>{cdr.auth_method || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>AUTH REFERENCE</td>
                        <td className='ps-0 pe-0 py-3'>
                          <small className='text-muted fw-semibold'>{cdr.authorization_reference || 'N/A'}</small>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Energy & Time Information */}
          <div className='col-md-6'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-success text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>ENERGY & TIME</h6>
              </div>
              <div className='card-body p-4'>
                <div className='table-responsive'>
                  <table className='table table-sm table-borderless align-middle gs-0 gy-2'>
                    <tbody>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>TOTAL ENERGY</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-primary fs-7 fw-bold'>{cdr.total_energy || 'N/A'} kWh</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>TOTAL TIME</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-info'>{cdr.total_time || 'N/A'} hours</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>TIME COST</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-warning'>{cdr.total_time_cost ? formatCurrency(cdr.total_time_cost, cdr.currency) : 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>CURRENCY</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-success fs-7 fw-bold'>{cdr.currency || 'N/A'}</span>
                        </td>
                      </tr>
                                             <tr>
                         <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>REMARK</td>
                         <td className='ps-0 pe-0 py-3'>
                           <span className='badge badge-light-warning fs-7 fw-bold'>{cdr.remark || 'N/A'}</span>
                         </td>
                       </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* CDR Token */}
          <div className='col-md-6'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-warning text-dark py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-dark'>CDR TOKEN</h6>
              </div>
              <div className='card-body p-4'>
                {renderCdrToken(cdr.cdr_token)}
              </div>
            </div>
          </div>

          {/* Total Cost */}
          <div className='col-md-6'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-info text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>COST BREAKDOWN</h6>
              </div>
              <div className='card-body p-4'>
                {renderTotalCost(cdr.total_cost)}
              </div>
            </div>
          </div>

          {/* CDR Location */}
          <div className='col-12'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-secondary text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>LOCATION DETAILS</h6>
              </div>
              <div className='card-body p-4'>
                {renderCdrLocation(cdr.cdr_location)}
              </div>
            </div>
          </div>

          {/* Tariffs */}
          <div className='col-12'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-dark text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>TARIFFS</h6>
              </div>
              <div className='card-body p-4'>
                {renderTariffs(cdr.tariffs)}
              </div>
            </div>
          </div>

          {/* Charging Periods */}
          <div className='col-12'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-primary text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>CHARGING PERIODS</h6>
              </div>
              <div className='card-body p-4'>
                {renderChargingPeriods(cdr.charging_periods)}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className='col-12'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-success text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>ADDITIONAL INFORMATION</h6>
              </div>
              <div className='card-body p-4'>
                <div className='row g-4'>
                  <div className='col-md-6'>
                    <div className='table-responsive'>
                      <table className='table table-sm table-borderless align-middle gs-0 gy-2'>
                        <tbody>
                          <tr className='border-bottom border-gray-200'>
                            <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>START DATE TIME</td>
                            <td className='ps-0 pe-0 py-3'>
                              <span className='fw-bold text-dark'>{formatDateTime(cdr.start_date_time)}</span>
                            </td>
                          </tr>
                          <tr className='border-bottom border-gray-200'>
                            <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>END DATE TIME</td>
                            <td className='ps-0 pe-0 py-3'>
                              <span className='fw-bold text-dark'>{formatDateTime(cdr.end_date_time)}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='table-responsive'>
                      <table className='table table-sm table-borderless align-middle gs-0 gy-2'>
                        <tbody>
                                                     <tr className='border-bottom border-gray-200'>
                             <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>REMARK</td>
                             <td className='ps-0 pe-0 py-3'>
                               <span className='badge badge-light-warning fs-7 fw-bold'>{cdr.remark || 'N/A'}</span>
                             </td>
                           </tr>
                          <tr>
                            <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>LAST UPDATED</td>
                            <td className='ps-0 pe-0 py-3'>
                              <span className='fw-bold text-dark'>{formatDateTime(cdr.last_updated)}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>OCPI CDRS</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>Manage and view charging data records</span>
        </h3>
      </div>
      
      <div className='card-body py-4'>
        <div className='table-responsive'>
          <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='min-w-200px ps-6 pe-6 py-4'>CDR ID</th>
                <th className='min-w-200px ps-6 pe-6 py-4'>SESSION ID</th>
                <th className='min-w-150px ps-6 pe-6 py-4'>START TIME</th>
                <th className='min-w-150px ps-6 pe-6 py-4'>END TIME</th>
                <th className='min-w-100px ps-6 pe-6 py-4'>ENERGY (KWH)</th>
                <th className='min-w-100px ps-6 pe-6 py-4'>COST</th>
                <th className='min-w-120px ps-6 pe-6 py-4'>REMARK</th>
                <th className='min-w-100px text-end ps-6 pe-6 py-4'>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {cdrs.map((cdr) => (
                <>
                  <tr key={cdr.id} className='border-bottom border-gray-200'>
                    <td className='ps-6 pe-6 py-4'>
                      <div className='d-flex align-items-center'>
                        <div className='d-flex justify-content-start flex-column'>
                          <span className='text-dark fw-bold text-hover-primary mb-1 fs-6'>
                            {cdr.id || 'N/A'}
                          </span>
                          <span className='text-muted fw-semibold text-muted d-block fs-7'>
                            {cdr.party_id} - {cdr.country_code}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                        {cdr.session_id || 'N/A'}
                      </span>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                        {formatDateTime(cdr.start_date_time)}
                      </span>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                        {formatDateTime(cdr.end_date_time)}
                      </span>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='badge badge-light-primary fs-7 fw-bold'>
                        {cdr.total_energy || 'N/A'} kWh
                      </span>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                        {cdr.total_cost ? formatCurrency(cdr.total_cost.incl_vat, cdr.total_cost.currency) : 'N/A'}
                      </span>
                    </td>
                                         <td className='ps-6 pe-6 py-4'>
                       <span className='badge badge-light-warning fs-7 fw-bold'>
                         {cdr.remark || 'N/A'}
                       </span>
                     </td>
                    <td className='text-end ps-6 pe-6 py-4'>
                      <button
                        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2'
                        onClick={() => setExpandedId(expandedId === cdr.id ? null : cdr.id)}
                        title={expandedId === cdr.id ? 'Hide Details' : 'View Details'}
                      >
                        <KTIcon iconName={expandedId === cdr.id ? 'eye-slash' : 'eye'} className='fs-3' />
                      </button>
                    </td>
                  </tr>

                  {/* Expanded details row */}
                  {expandedId === cdr.id && (
                    <tr>
                      <td colSpan={8} className='p-0'>
                        {renderCdrDetails(cdr)}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination */}
      <div className='card-footer'>
        <PaginatedItems setPage={setPage} pages={pages} currentPage={page} />
      </div>
    </div>
  )
}
